/**
 * The frame's own reads (who am I, the outlet's bundle) with the offline store behind
 * them, and what a signed-in till does to stay ready for the cloud going away: keep the
 * PIN verifiers, the invoice blocks and the KOT ranges topped up.
 */
import { unwrap, type Me, type OutletBootstrap, type TokenPair } from '@qrkey/api-client';
import type { PriceContext } from '@qrkey/contracts';
import { api } from '../app/api.js';
import { cachedRead, keys } from './cache.js';
import { connectivity } from './connectivity.js';
import { kvGet, kvSet } from './db.js';
import { OfflineRefusal } from './refusal.js';
import { topUp } from './numbers.js';
import { refreshOfflinePins } from './pin.js';
import { isOfflineSession, offlinePerson } from './session.js';

/** `GET /v1/me`; for a person signed in offline, built from the PIN bundle's permissions. */
export async function readMe(session: TokenPair): Promise<Me> {
  const person = offlinePerson();
  if (isOfflineSession(session) && person) {
    const outletId = session.session.outletId ?? '';
    return {
      user: {
        id: person.userId,
        name: person.name,
        phone: null,
        email: null,
        isPlatformStaff: false,
        totpEnabled: false,
        hasPassword: false,
      },
      companyId: session.session.companyId,
      session: session.session,
      memberships: [
        { id: person.userId, outletId, roleId: person.roleId, roleName: person.roleName },
      ],
      outlets: [{ outletId, permissions: [...person.permissions] }],
      permissions: [],
    } as unknown as Me;
  }
  return cachedRead(keys.me(session.session.userId), () => unwrap(api.client.GET('/v1/me')));
}

/** `GET {o}/bootstrap`, remembered for the offline POS (and for people who sign in offline). */
export async function readBootstrap(
  outletId: string,
  session: TokenPair,
): Promise<OutletBootstrap> {
  const person = offlinePerson();
  if (isOfflineSession(session) && person) {
    const latest = await kvGet<OutletBootstrap>(keys.bootstrapLatest(outletId));
    if (!latest) {
      throw new OfflineRefusal(
        'NOT_CACHED',
        'This till has not loaded the outlet yet. Sign in while online once.',
      );
    }
    return { ...latest, permissions: [...person.permissions] as never };
  }
  const bundle = await cachedRead(keys.bootstrap(outletId, session.session.userId), () =>
    unwrap(api.client.GET('/v1/outlets/{outletId}/bootstrap', { params: { path: { outletId } } })),
  );
  void kvSet(keys.bootstrapLatest(outletId), bundle);
  return bundle;
}

/**
 * The price lists the till prices items from: one per section's pricing context, and the
 * takeaway and delivery menus. The POS fetches the one it shows; this makes sure the others
 * are on the device before the cloud goes away.
 */
export async function prefetchMenus(outletId: string): Promise<void> {
  const bundle = await kvGet<OutletBootstrap>(keys.bootstrapLatest(outletId));
  // Once the published menu is on the device, again only when it changes or after a while.
  const stamp = `menus-prefetched:${outletId}`;
  const last = await kvGet<{ at: number; menuVersion: number | null }>(stamp);
  if (
    last &&
    last.menuVersion === (bundle?.menuVersion ?? null) &&
    Date.now() - last.at < 600_000
  ) {
    return;
  }
  const wanted: { context: PriceContext; sectionId: string | null }[] = [
    { context: 'takeaway', sectionId: null },
    { context: 'delivery', sectionId: null },
    ...(bundle?.sections ?? [])
      .filter((s) => s.archivedAt === null)
      .map((s) => ({ context: s.pricingContext as PriceContext, sectionId: s.id })),
  ];
  for (const w of wanted) {
    try {
      const menu = await unwrap(
        api.client.GET('/v1/outlets/{outletId}/menu', {
          params: {
            path: { outletId },
            query: { context: w.context, ...(w.sectionId ? { sectionId: w.sectionId } : {}) },
          },
        }),
      );
      await kvSet(keys.menu(outletId, w.context, w.sectionId), menu);
    } catch {
      // Not served here (e.g. delivery off): the till simply has no such menu offline.
    }
  }
  await kvSet(stamp, { at: Date.now(), menuVersion: bundle?.menuVersion ?? null });
}

/** A till with a real session, online: keep offline sign-in and the number runs ready. */
export async function prepareOffline(outletId: string, terminalId: string): Promise<void> {
  if (!connectivity.isOnline()) return;
  await Promise.allSettled([
    refreshOfflinePins(outletId),
    topUp(outletId, terminalId),
    prefetchMenus(outletId),
  ]);
}
